import * as css from 'css/exports.module.scss';

import './JiraIssue.scss';

import { createAsyncSelectors } from '@lib/selectors';
import flatMap from 'lodash/flatMap';
import { JiraIssueMappingTooltip } from './JiraIssueMappingTooltip';
import { JiraIssuesOptions } from './JiraIssueOptions';
import { jiraSummarySelectors } from '../../../settings/jira/@arch/selectors';
import { SonarText } from '@ui/text';
import { trimEnd } from 'lodash';

import { Case, Default, Switch, When } from 'react-if';
import { CloseCircleOutlined, ExclamationCircleOutlined, MoreOutlined, SyncOutlined } from '@ant-design/icons';
import { Popover, Row, Tag, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import { useCurrentUser, useJiraStatuses, useSonarSelector } from '@hooks';

export const jiraSyncSelector = createAsyncSelectors((state) => state.integrations.jira.initiatives.sync.post);

export interface JiraIssueProps {
	initiativeId: number;
	issueKey: string;
	issueName: string;
	isSynced?: boolean;
	projectKey: string;
	statusId: string;
	summary?: boolean;
}

export const JiraIssue: React.FC<JiraIssueProps> = ({
	initiativeId,
	issueKey,
	issueName,
	isSynced,
	projectKey,
	statusId,
	summary
}) => {
	const [ open, setOpen ] = useState(false);
	const jiraStatuses = useJiraStatuses();
	const { isReadOnly } = useCurrentUser();
	const issueStatus = jiraStatuses.filter((status) => status.id === statusId)?.[ 0 ];
	const isRequested = useSonarSelector(jiraSyncSelector.isRequested);
	const jiraSummary = useSonarSelector(jiraSummarySelectors.data);

	const allStatuses = useMemo(() => flatMap(
		jiraSummary?.selectedProjects
			.filter((project) => project.projectKey === projectKey)[ 0 ]
			?.issueTypes.map(({ statuses }) => statuses) ?? []
	),
	[ jiraSummary?.selectedProjects, projectKey ]);

	const jiraIssueURL = useMemo(() =>
		`${trimEnd(jiraSummary?.url ?? '', '/')}/browse/${issueKey}`, [ issueKey, jiraSummary?.url ]);

	const { incompleteMapping, missingMapping } = useMemo(
		() => ({
			incompleteMapping: allStatuses.some((statusMap) => !statusMap.initiativeStatusId),
			missingMapping: allStatuses.some(
				(statusMap) => statusMap.jiraStatusId === issueStatus?.id && !statusMap.initiativeStatusId
			)
		}),
		[ allStatuses, issueStatus?.id ]
	);

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
	};

	const hide = () => {
		setOpen(false);
	};

	return (
		<Row
			gutter={ Number(css.size32) }
			id='jira-issue'
			wrap={ false }>
			<div className='jira-label'>
				<a
					className='issue-key'
					href={ jiraIssueURL }
					target='_blank'>
					{ issueKey }
				</a>
				<When condition={ jiraStatuses.length }>
					<Tag>{ issueStatus?.name }</Tag>
				</When>
				<SonarText className='issue-name'>{ issueName }</SonarText>
			</div>
			<When condition={ summary }>
				<Switch>
					<Case condition={ isSynced && (jiraStatuses.length === 0 || isRequested) }>
						<Tooltip
							placement='bottom'
							title={ 'Loading' }>
							<SyncOutlined className='sync-icon loading' />
						</Tooltip>
					</Case>
					<Case condition={ isSynced && missingMapping }>
						<Tooltip
							placement='bottom'
							title={ <JiraIssueMappingTooltip missingMapping /> }>
							<CloseCircleOutlined className='sync-icon missing' />
						</Tooltip>
					</Case>
					<Case condition={ isSynced && incompleteMapping }>
						<Tooltip title={ <JiraIssueMappingTooltip /> }>
							<ExclamationCircleOutlined className='sync-icon incomplete' />
						</Tooltip>
					</Case>
					<Case condition={ isSynced }>
						<SyncOutlined className='sync-icon' />
					</Case>
					<Default />
				</Switch>
				<When condition={ !isReadOnly }>
					<Popover
						classNames={ { root: 'issue-options' } }
						content={
							<JiraIssuesOptions
								hide={ hide }
								initiativeId={ initiativeId }
								issueKey={ issueKey }
								isSynced={ isSynced } />
						}
						onOpenChange={ handleOpenChange }
						open={ open }
						placement='bottomRight'
						trigger='click'>
						<MoreOutlined className='issue-more' />
					</Popover>
				</When>
			</When>
		</Row>
	);
};
